import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setTestType, getTestType, type TestParams } from 'store/slices/test';
import { TestType } from 'types/testType';
import { URLParamKey } from 'constants/urlParams';
import { getParamCaseInsensitive } from 'utils/urlParams.util';
import { sentryLogMessage, sentryLogException } from 'services/sentryLogger';
import { SENTRY_EVENT_TYPES } from 'constants/sentryEvents';
import { isValidSessionId, isSessionStartLogged, markSessionStartLogged } from 'services/sessionPersistence';
import { deriveSessionIdFromToken, setLeumitToken } from 'services/leumitSession';

const DEFAULT_TEST_TYPE = TestType.FOB;

/** Returned when `sessionId` contains characters outside `a-z`, `A-Z`, `0-9`, or `_`. */
const SESSION_ID_ERROR = 'SessionIdError';

/**
 * Validates and normalizes a testType string to a TestType enum value
 * @param value - The raw string value from URL
 * @returns The validated TestType enum value or default
 */
const validateTestType = (value: string | null): TestType => {
  if (!value) {
    return DEFAULT_TEST_TYPE;
  }

  const normalized = value.toLowerCase().trim();

  // Check against all TestType enum values
  const testTypeValues = Object.values(TestType) as Array<string>;
  
  if (testTypeValues.includes(normalized)) {
    return normalized as TestType;
  }

  console.warn(
    `[useTestParams] Invalid testType value: "${value}", using default: ${DEFAULT_TEST_TYPE}`
  );
  return DEFAULT_TEST_TYPE;
};

const ALPHANUMERIC_SESSION_ID_PATTERN = /^[a-zA-Z0-9_]+$/;

/**
 * Validates `sessionId` from the URL: only ASCII letters, digits, and underscores allowed.
 * Empty or whitespace-only values are treated as absent (`null`).
 * @param value - Raw query value or `null` when the param is missing
 * @returns Trimmed valid id, `null` if missing/empty, or `SessionIdError` if invalid
 */
const validateSessionId = (value: string | null): string | null => {
  if (value === null) {
    return null;
  }

  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return null;
  }

  if (ALPHANUMERIC_SESSION_ID_PATTERN.test(trimmed)) {
    return trimmed;
  }

  const invalidSessionError = new Error(
    `Invalid sessionId URL parameter (${SESSION_ID_ERROR})`
  );

  sentryLogException({
    error: invalidSessionError,
    tags: {
      component: 'useTestParams',
      category: 'sessionIdValidation',
      errorCode: SESSION_ID_ERROR,
    },
    extra: {
      errorCode: SESSION_ID_ERROR,
      rawSessionId: value,
      timestamp: new Date().toISOString(),
    },
  });

  console.warn(
    `[useTestParams] errorCode=${SESSION_ID_ERROR} — sessionId must be a-z, A-Z, 0-9, _ only; raw value: "${value}"`
  );
  return SESSION_ID_ERROR;
};

interface ParsedUrlParams extends TestParams {
  token: string | null;
}

/**
 * Extracts and validates test parameters from URL search string
 * Supports case-insensitive parameter names (e.g., testType, testtype, TESTTYPE)
 * @param search - URL search string (e.g., "?testType=hpylori&sessionId=abc123")
 * @returns Object with testType, sessionId, and token
 */
const parseTestParamsFromURL = (search: string): ParsedUrlParams => {
  const queryParams = new URLSearchParams(search);

  // Extract testType (case-insensitive - supports testType, testtype, TESTTYPE, etc.)
  const testTypeParam = getParamCaseInsensitive(queryParams, URLParamKey.TEST_TYPE);
  const testType = validateTestType(testTypeParam);

  // Extract sessionId ( case-insensitive - supports sessionId, sessionid, SESSIONID, etc.)
  const rawSessionId = getParamCaseInsensitive(queryParams, URLParamKey.SESSION_ID);
  const sessionId = validateSessionId(rawSessionId);

  // Extract token (case-insensitive - supports token, Token, TOKEN, etc.)
  const rawToken = getParamCaseInsensitive(queryParams, URLParamKey.TOKEN);
  const token = rawToken?.trim() || null;

  return {
    testType,
    sessionId,
    token,
  };
};

/**
 * Logs SESSION_START once per valid sessionId (survives WebView reload via localStorage).
 */
const logSessionStartIfNeeded = (testType: TestType, sessionId: string | null): void => {
  if (!sessionId || !isValidSessionId(sessionId) || isSessionStartLogged(sessionId)) {
    return;
  }

  markSessionStartLogged(sessionId);
  sentryLogMessage({
    message: 'Session started - QR scanned and logged in',
    level: 'info',
    tags: {
      component: 'useTestParams',
      category: 'session_start',
      testType,
      event_type: SENTRY_EVENT_TYPES.SESSION_START,
    },
    extra: {
      sessionId,
      timestamp: new Date().toISOString(),
    },
  });
};

/**
 * Custom hook for managing test parameters from URL
 * - Parses testType, sessionId, and token from URL search params
 * - When a JWT token is present (Leumit iframe), derives sessionId via SHA-256
 * - Syncs them to Redux store
 * - Provides access to current test params
 * 
 * @returns Object containing testType and sessionId
 */
const useTestParams = () => {
  const { search } = useLocation();
  const dispatch = useAppDispatch();
  const lastDispatchedParams = useRef<string | null>(null);
  const storeParams = useAppSelector(getTestType);

  // Parse URL params and sync to Redux
  useEffect(() => {
    let cancelled = false;

    const syncParams = async (): Promise<void> => {
      const urlParams = parseTestParamsFromURL(search);
      let sessionId = urlParams.sessionId;

      // Leumit iframe: persist JWT and derive sessionId from token
      if (urlParams.token) {
        setLeumitToken(urlParams.token);

        try {
          sessionId = await deriveSessionIdFromToken(urlParams.token);
        } catch (error) {
          // Never log the raw JWT
          sentryLogException({
            error,
            tags: {
              component: 'useTestParams',
              category: 'sessionIdDerivation',
            },
            extra: {
              hasToken: true,
              timestamp: new Date().toISOString(),
            },
          });
          console.warn('[useTestParams] Failed to derive sessionId from token; falling back to URL sessionId');
          // Fall back to URL sessionId (may be null)
        }
      }

      if (cancelled) {
        return;
      }

      const paramsKey = `${urlParams.testType}-${sessionId || 'null'}`;

      if (paramsKey !== lastDispatchedParams.current) {
        lastDispatchedParams.current = paramsKey;
        dispatch(setTestType({
          testType: urlParams.testType,
          sessionId,
        }));

        logSessionStartIfNeeded(urlParams.testType, sessionId);
      }
    };

    syncParams();

    return () => {
      cancelled = true;
    };
  }, [search, dispatch]);

  // Check if should move directly to camera step (case-insensitive)
  const shouldMoveToCamera = useCallback((): boolean => {
    const queryParams = new URLSearchParams(search);
    return getParamCaseInsensitive(queryParams, URLParamKey.MOVE_TO_CAMERA) === 'true';
  }, [search]);

  return {
    testType: storeParams.testType,
    sessionId: storeParams.sessionId,
    shouldMoveToCamera,
  };
};

export default useTestParams;

